import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarCheck, Check, Copy, Download, Loader2, ListChecks, Plus, RefreshCw, ScanSearch, LogOut, Newspaper, PenLine, ScrollText, Search, Trash2, TrendingUp, ExternalLink, X } from "lucide-react";
import { SALES_SCRIPTS } from "@/data/salesScripts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function NoteCell({ lead, onSave }) {
  const [note, setNote] = useState(lead.note || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const next = note.trim();
    if (saving || next === (lead.note || "")) return;
    setSaving(true);
    await onSave(lead, next, () => setNote(lead.note || ""));
    setSaving(false);
  };

  return (
    <div className="relative">
      <input
        data-testid="dash-lead-note-input"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.target.blur();
        }}
        placeholder="Add a note…"
        className="w-full min-w-[160px] rounded-lg border border-slate-200 bg-white/[0.05] px-3 py-2 text-base text-slate-900 placeholder:text-slate-700 outline-none transition-colors focus:border-flame"
      />
      {saving && (
        <Loader2 className="absolute right-2 top-2.5 h-3.5 w-3.5 animate-spin text-flame" />
      )}
    </div>
  );
}

export default function LeadsDashboard() {
  const [token, setToken] = useState(
    () => sessionStorage.getItem("zd_dash_token") || ""
  );
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [leads, setLeads] = useState(null);
  const [bookings, setBookings] = useState(null);
  const [subs, setSubs] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [posts, setPosts] = useState(null);
  const [openScript, setOpenScript] = useState(null);
  const [copied, setCopied] = useState(null);
  const [wins, setWins] = useState(null);
  const [winText, setWinText] = useState({ traffic: "", seo: "", conversions: "" });
  const [winBusy, setWinBusy] = useState("");
  const [hlToken, setHlToken] = useState("");
  const [google, setGoogle] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const syncGoogle = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const t = sessionStorage.getItem("zd_dash_token") || "";
      await axios.post(`${API}/google/sync`, {}, { headers: { Authorization: `Bearer ${t}` } });
      const [w, g] = await Promise.all([
        axios.get(`${API}/wins/all?token=${encodeURIComponent(t)}`),
        axios.get(`${API}/google/status?token=${encodeURIComponent(t)}`),
      ]);
      setWins(w.data);
      setGoogle(g.data);
    } catch (e) {} finally {
      setSyncing(false);
    }
  };

  const connectGoogle = () => {
    const t = sessionStorage.getItem("zd_dash_token") || "";
    window.location.href = `${API}/google/connect?token=${encodeURIComponent(t)}&origin=${encodeURIComponent(window.location.origin)}`;
  };

  const daysAgo = (iso) =>
    (Date.now() - new Date(iso).getTime()) / 86400000;

  const WEEK = 7 * 86400000;
  const inLast = (iso, days) => Date.now() - new Date(iso).getTime() < days * 86400000;
  const leadsSafe = leads || [];
  const leadsThisWeek = leadsSafe.filter((x) => inLast(x.created_at, 7)).length;
  const leadsLastWeek = leadsSafe.filter((x) => {
    const d = Date.now() - new Date(x.created_at).getTime();
    return d >= WEEK && d < 2 * WEEK;
  }).length;
  const leadsDelta = leadsThisWeek - leadsLastWeek;
  const bookings30 = (bookings || []).filter((x) => inLast(x.created_at, 30)).length;

  const addWin = async (bucket) => {
    const text = (winText[bucket] || "").trim();
    if (winBusy || text.length < 3) return;
    setWinBusy(bucket);
    try {
      const t = sessionStorage.getItem("zd_dash_token") || "";
      const doc = await axios.post(
        `${API}/wins`,
        { bucket, text },
        { headers: { Authorization: `Bearer ${t}` } }
      );
      setWins((prev) => [doc.data, ...(prev || [])]);
      setWinText((prev) => ({ ...prev, [bucket]: "" }));
    } catch (e) {} finally {
      setWinBusy("");
    }
  };

  const deleteWin = async (id, bucket) => {
    if (winBusy) return;
    setWinBusy(bucket + id);
    try {
      const t = sessionStorage.getItem("zd_dash_token") || "";
      await axios.delete(`${API}/wins/${id}?token=${encodeURIComponent(t)}`);
      setWins((prev) => (prev || []).filter((w) => w.id !== id));
    } catch (e) {} finally {
      setWinBusy("");
    }
  };

  const copyScript = (s) => {
    const text = `Subject: ${s.subject}\n\n${s.body}`;
    const done = () => {
      setCopied(s.id);
      setTimeout(() => setCopied((c) => (c === s.id ? null : c)), 2000);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(done);
    } else {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      done();
    }
  };
  const [editingId, setEditingId] = useState(null);
  const [postForm, setPostForm] = useState({ title: "", body: "", published: true });
  const [savingPost, setSavingPost] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showAll, setShowAll] = useState(true);

  const load = async (t) => {
    setRefreshing(true);
    try {
      const [l, b, n, p, c, w, g, h] = await Promise.all([
        axios.get(`${API}/leads/all?token=${encodeURIComponent(t)}`),
        axios.get(`${API}/bookings/all?token=${encodeURIComponent(t)}`),
        axios.get(`${API}/newsletter/all?token=${encodeURIComponent(t)}`),
        axios.get(`${API}/blog/admin?token=${encodeURIComponent(t)}`),
        axios.get(`${API}/checklist/all?token=${encodeURIComponent(t)}`),
        axios.get(`${API}/wins/all?token=${encodeURIComponent(t)}`),
        axios.get(`${API}/google/status?token=${encodeURIComponent(t)}`),
        axios.get(`${API}/hl/webhook-token?token=${encodeURIComponent(t)}`),
      ]);
      setLeads(
        l.data.map((x) => ({
          ...x,
          contacted: !!x.contacted,
          note: x.note || "",
        }))
      );
      setBookings(b.data);
      setSubs(n.data);
      setChecklist(c.data);
      setPosts(p.data);
      setWins(w.data);
      setGoogle(g.data);
      setHlToken((h.data && h.data.token) || "");
    } catch (e) {
      if (e.response && e.response.status === 401) {
        sessionStorage.removeItem("zd_dash_token");
        setToken("");
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      }
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startEditPost = (p) => {
    setEditingId(p.id);
    setPostForm({ title: p.title, body: p.body, published: !!p.published });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resetPostForm = () => {
    setEditingId(null);
    setPostForm({ title: "", body: "", published: true });
  };

  const savePost = async (e) => {
    e.preventDefault();
    if (savingPost) return;
    setSavingPost(true);
    try {
      if (editingId) {
        await axios.put(`${API}/blog/${editingId}`, postForm, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Post updated");
      } else {
        await axios.post(`${API}/blog`, postForm, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Post published");
      }
      resetPostForm();
      const { data } = await axios.get(
        `${API}/blog/admin?token=${encodeURIComponent(token)}`
      );
      setPosts(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingPost(false);
    }
  };

  const deletePost = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/blog/${p.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPosts((rows) => (rows || []).filter((r) => r.id !== p.id));
      if (editingId === p.id) resetPostForm();
      toast.success("Post deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const login = async (e) => {
    e.preventDefault();
    if (loggingIn) return;
    setLoggingIn(true);
    setLoginError("");
    try {
      const { data } = await axios.post(`${API}/leads/login`, { password });
      sessionStorage.setItem("zd_dash_token", data.token);
      setToken(data.token);
      load(data.token);
    } catch (err) {
      setLoginError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem("zd_dash_token");
    setToken("");
    setLeads(null);
    setBookings(null);
    setSubs(null);
    setChecklist(null);
    setPosts(null);
    setWins(null);
    setGoogle(null);
    setEditingId(null);
    setPostForm({ title: "", body: "", published: true });
    setShowAll(true);
  };

  const toggleContacted = async (lead, index) => {
    const next = !lead.contacted;
    setLeads((rows) =>
      rows.map((r, i) => (i === index ? { ...r, contacted: next } : r))
    );
    try {
      await axios.post(
        `${API}/leads/contacted`,
        { id: lead.id, contacted: next },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      setLeads((rows) =>
        rows.map((r, i) => (i === index ? { ...r, contacted: !next } : r))
      );
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const saveNote = async (lead, note, revert) => {
    try {
      await axios.post(
        `${API}/leads/notes`,
        { id: lead.id, note },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLeads((rows) =>
        rows.map((r) => (r.id === lead.id ? { ...r, note } : r))
      );
      toast.success("Note saved");
    } catch (err) {
      revert();
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const weekAgo = Date.now() - 7 * 24 * 3600 * 1000;
  const recentLeads = (leads || []).filter(
    (l) => new Date(l.created_at).getTime() > weekAgo
  ).length;
  const recentBookings = (bookings || []).filter(
    (b) => new Date(b.created_at).getTime() > weekAgo
  ).length;
  const uncontacted = (leads || []).filter((l) => !l.contacted).length;
  const visibleLeads = showAll ? leads || [] : (leads || []).filter((l) => !l.contacted);

  if (!token) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-obsidian px-4 py-24">
        <form
          data-testid="dash-login-form"
          onSubmit={login}
          className="glass w-full max-w-sm rounded-2xl border border-slate-200 p-8"
        >
          <p className="font-mono text-[13px] font-medium uppercase tracking-[0.3em] text-flame">
            ZD·Design — Studio
          </p>
          <h1 className="mt-3 font-display text-2xl font-bold text-slate-900">
            ZD Design Marketing Dashboard
          </h1>
          <label
            htmlFor="dash-password"
            className="mt-6 block font-mono text-base font-medium uppercase tracking-[0.22em] text-slate-800"
          >
            Password
          </label>
          <input
            id="dash-password"
            data-testid="dash-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••••••"
            className="mt-2 w-full rounded-lg border border-slate-300 bg-slate-50 px-4 py-3 text-lg text-slate-900 placeholder:text-slate-700 outline-none transition-colors focus:border-flame"
          />
          {loginError && (
            <p
              data-testid="dash-login-error"
              className="mt-3 text-base text-red-300"
            >
              {loginError}
            </p>
          )}
          <button
            data-testid="dash-login-button"
            type="submit"
            disabled={loggingIn}
            className="mt-5 w-full rounded-2xl bg-flame px-6 py-3.5 font-mono text-base font-semibold uppercase tracking-[0.15em] text-obsidian transition-all duration-300 hover:shadow-[0_0_32px_rgba(255,85,0,0.45)] disabled:opacity-60"
          >
            {loggingIn ? "Checking…" : "Open dashboard"}
          </button>
        </form>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-obsidian px-4 py-24 sm:px-8">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-mono text-[13px] font-medium uppercase tracking-[0.3em] text-flame">
              ZD·Design — Studio
            </p>
            <h1 className="mt-2 font-display text-3xl font-bold tracking-tight text-slate-900">
              ZD Design Marketing Dashboard
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="dash-refresh-button"
              onClick={() => load(token)}
              className="flex items-center gap-2 rounded-2xl border border-slate-300 px-5 py-2.5 font-mono text-base font-semibold uppercase tracking-[0.15em] text-slate-900 transition-colors hover:border-flame/60 hover:text-flame"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              data-testid="dash-logout-button"
              onClick={logout}
              className="flex items-center gap-2 rounded-2xl border border-slate-300 px-5 py-2.5 font-mono text-base font-semibold uppercase tracking-[0.15em] text-slate-900 transition-colors hover:border-flame/60 hover:text-flame"
            >
              <LogOut className="h-3.5 w-3.5" />
              Log out
            </button>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
          {[
            { label: "Snapshot leads — all time", value: leads ? leads.length : "…" },
            { label: "Snapshot leads — 7 days", value: leads ? recentLeads : "…" },
            { label: "Bookings — all time", value: bookings ? bookings.length : "…" },
            { label: "Bookings — 7 days", value: bookings ? recentBookings : "…" },
          ].map((s) => (
            <div
              key={s.label}
              data-testid="dash-stat"
              className="glass rounded-2xl border border-slate-200 p-5"
            >
              <div className="font-display text-2xl font-bold text-slate-900">
                {s.value}
              </div>
              <div className="mt-1 font-mono text-base uppercase tracking-[0.15em] text-faint">
                {s.label}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <a
            data-testid="dash-download-leads-csv"
            href={`${API}/leads/export?token=${encodeURIComponent(token)}`}
            download
            className="flex items-center gap-2 rounded-2xl bg-flame px-5 py-2.5 font-mono text-base font-semibold uppercase tracking-[0.15em] text-obsidian transition-all duration-300 hover:shadow-[0_0_28px_rgba(255,85,0,0.45)]"
          >
            <Download className="h-3.5 w-3.5" />
            Leads CSV
          </a>
          <a
            data-testid="dash-download-bookings-csv"
            href={`${API}/bookings/export?token=${encodeURIComponent(token)}`}
            download
            className="flex items-center gap-2 rounded-2xl bg-flame px-5 py-2.5 font-mono text-base font-semibold uppercase tracking-[0.15em] text-obsidian transition-all duration-300 hover:shadow-[0_0_28px_rgba(255,85,0,0.45)]"
          >
            <Download className="h-3.5 w-3.5" />
            Bookings CSV
          </a>
        </div>

        <section className="mt-10">
          <h2 className="flex items-center gap-3 font-display text-xl font-bold text-slate-900">
            <CalendarCheck className="h-5 w-5 text-flame" />
            Strategy call bookings
          </h2>
          {bookings === null ? (
            <p data-testid="dash-loading" className="mt-4 flex items-center gap-2 text-base text-faint">
              <Loader2 className="h-4 w-4 animate-spin text-flame" /> Loading…
            </p>
          ) : bookings.length === 0 ? (
            <p data-testid="dash-empty-bookings" className="mt-4 rounded-xl border border-slate-200 bg-coal/60 px-5 py-6 text-base text-mist">
              No bookings yet — when someone picks a time on the site, it shows up here.
            </p>
          ) : (
            <div data-testid="dash-bookings-table" className="glass mt-4 overflow-x-auto rounded-2xl border border-slate-200">
              <table className="w-full min-w-[560px] text-left">
                <thead>
                  <tr className="border-b border-slate-200">
                    {["Name", "Email", "Slot (CT)", "Booked on"].map((h) => (
                      <th key={h} className="px-4 py-3 font-mono text-base font-medium uppercase tracking-[0.15em] text-faint">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b, i) => (
                    <tr key={i} data-testid={`dash-booking-row-${i}`} className="border-b border-slate-100 last:border-0">
                      <td className="px-4 py-3 text-base font-semibold text-slate-900">{b.name}</td>
                      <td className="px-4 py-3 text-base text-mist">{b.email}</td>
                      <td className="px-4 py-3 text-base text-flame">{b.date} · {b.time} CT</td>
                      <td className="px-4 py-3 text-base text-faint">{String(b.created_at || "").slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="mt-10 pb-16">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <h2 className="flex items-center gap-3 font-display text-xl font-bold text-slate-900">
              <ScanSearch className="h-5 w-5 text-flame" />
              Snapshot leads
            </h2>
            <div className="flex items-center gap-1 rounded-2xl border border-slate-200 bg-coal/70 p-1">
              {[
                { label: `All (${leads ? leads.length : 0})`, val: true },
                { label: `Not contacted (${uncontacted})`, val: false },
              ].map((opt) => (
                <button
                  key={opt.val}
                  data-testid={`dash-lead-filter-${opt.val ? "all" : "uncontacted"}`}
                  onClick={() => setShowAll(opt.val)}
                  className={`rounded-2xl px-4 py-1.5 font-mono text-base uppercase tracking-[0.12em] transition-colors ${
                    showAll === opt.val
                      ? "bg-flame text-obsidian"
                      : "text-mist hover:text-flame"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <p className="mt-2 font-mono text-base text-faint">
            Tick the box once you've called — notes save when you click away.
          </p>
          {leads === null ? (
            <p className="mt-4 flex items-center gap-2 text-base text-faint">
              <Loader2 className="h-4 w-4 animate-spin text-flame" /> Loading…
            </p>
          ) : visibleLeads.length === 0 ? (
            <p data-testid="dash-empty-leads" className="mt-4 rounded-xl border border-slate-200 bg-coal/60 px-5 py-6 text-base text-mist">
              {showAll
                ? "No snapshot leads yet — every run of the free tool lands here."
                : "Every lead has been contacted. Great week."}
            </p>
          ) : (
            <div data-testid="dash-leads-table" className="glass mt-4 overflow-x-auto rounded-2xl border border-slate-200">
              <table className="w-full min-w-[900px] text-left">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="px-4 py-3 font-mono text-base font-medium uppercase tracking-[0.15em] text-faint">Called</th>
                    {["Email", "Website", "Verdict", "Note", "Date"].map((h) => (
                      <th key={h} className="px-4 py-3 font-mono text-base font-medium uppercase tracking-[0.15em] text-faint">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleLeads.map((l) => {
                    const i = leads.indexOf(l);
                    return (
                      <tr
                        key={l.id || i}
                        data-testid={`dash-lead-row-${i}`}
                        className={`border-b border-slate-100 last:border-0 transition-opacity ${
                          l.contacted ? "opacity-45" : ""
                        }`}
                      >
                        <td className="px-4 py-3">
                          <button
                            data-testid={`dash-lead-checkbox-${i}`}
                            aria-label={l.contacted ? "Mark as not contacted" : "Mark as contacted"}
                            onClick={() => toggleContacted(l, i)}
                            className={`flex h-5 w-5 items-center justify-center rounded border transition-all duration-200 ${
                              l.contacted
                                ? "border-flame bg-flame text-obsidian"
                                : "border-white/25 bg-white/[0.05] hover:border-flame/60"
                            }`}
                          >
                            {l.contacted && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-base font-semibold text-slate-900">
                          {l.email}
                          {l.contacted && (
                            <span className="ml-2 rounded-full border border-flame/40 bg-flame/10 px-2 py-0.5 font-mono text-[13px] uppercase tracking-[0.1em] text-flame">
                              Contacted
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-base text-mist">{l.url}</td>
                        <td className="max-w-[220px] px-4 py-3 text-base leading-snug text-mist">{l.verdict}</td>
                        <td className="px-4 py-3">
                          <NoteCell lead={l} onSave={saveNote} />
                        </td>
                        <td className="px-4 py-3 text-base text-faint">{String(l.created_at || "").slice(0, 10)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
        <section className="mt-10">
          <h2 className="flex items-center gap-3 font-display text-xl font-bold text-slate-900">
            <Newspaper className="h-5 w-5 text-flame" />
            Newsletter subscribers
            <span className="rounded-full border border-slate-200 bg-coal/70 px-3 py-1 font-mono text-[13px] uppercase tracking-[0.12em] text-mist">
              {subs ? subs.length : "…"}
            </span>
          </h2>
          {subs === null ? (
            <p className="mt-4 flex items-center gap-2 text-base text-faint">
              <Loader2 className="h-4 w-4 animate-spin text-flame" /> Loading…
            </p>
          ) : subs.length === 0 ? (
            <p
              data-testid="dash-empty-subs"
              className="mt-4 rounded-xl border border-slate-200 bg-coal/60 px-5 py-6 text-base text-mist"
            >
              No subscribers yet — footer signups land here.
            </p>
          ) : (
            <div
              data-testid="dash-subs-table"
              className="glass mt-4 overflow-x-auto rounded-2xl border border-slate-200"
            >
              <table className="w-full min-w-[420px] text-left">
                <thead>
                  <tr className="border-b border-slate-200">
                    {["Email", "Subscribed on"].map((h) => (
                      <th key={h} className="px-4 py-3 font-mono text-base font-medium uppercase tracking-[0.15em] text-faint">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {subs.map((s, i) => (
                    <tr key={s.id || i} data-testid={`dash-sub-row-${i}`} className="border-b border-slate-100 last:border-0">
                      <td className="px-4 py-3 text-base font-semibold text-slate-900">{s.email}</td>
                      <td className="px-4 py-3 text-base text-faint">{String(s.created_at || "").slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="mt-10">
          <h2 className="flex items-center gap-3 font-display text-xl font-bold text-slate-900">
            <ListChecks className="h-5 w-5 text-flame" />
            AI checklist sign-ups
            <span className="rounded-full border border-slate-200 bg-coal/70 px-3 py-1 font-mono text-[13px] uppercase tracking-[0.12em] text-mist">
              {checklist ? checklist.length : "…"}
            </span>
          </h2>
          <p className="mt-2 font-mono text-base text-faint">
            People who emailed themselves the AI answer checklist — opted in to hear more about AI. Click an address to follow up.
          </p>
          {checklist === null ? (
            <p className="mt-4 flex items-center gap-2 text-base text-faint">
              <Loader2 className="h-4 w-4 animate-spin text-flame" /> Loading…
            </p>
          ) : checklist.length === 0 ? (
            <p
              data-testid="dash-empty-checklist"
              className="mt-4 rounded-xl border border-slate-200 bg-coal/60 px-5 py-6 text-base text-mist"
            >
              No sign-ups yet — every "email me the checklist" on the checklist page lands here.
            </p>
          ) : (
            <div
              data-testid="dash-checklist-table"
              className="glass mt-4 overflow-x-auto rounded-2xl border border-slate-200"
            >
              <table className="w-full min-w-[520px] text-left">
                <thead>
                  <tr className="border-b border-slate-200">
                    {["Email", "Opted in", "Last requested"].map((h) => (
                      <th key={h} className="px-4 py-3 font-mono text-base font-medium uppercase tracking-[0.15em] text-faint">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {checklist.map((s, i) => (
                    <tr key={s.id || i} data-testid={`dash-checklist-row-${i}`} className="border-b border-slate-100 last:border-0">
                      <td className="px-4 py-3 text-base font-semibold text-slate-900">
                        <a
                          href={`mailto:${s.email}`}
                          data-testid={`dash-checklist-mail-${i}`}
                          className="transition-colors hover:text-flame"
                        >
                          {s.email}
                        </a>
                      </td>
                      <td className="px-4 py-3 text-base text-faint">{String(s.created_at || "").slice(0, 10)}</td>
                      <td className="px-4 py-3 text-base text-faint">{String(s.last_requested_at || s.created_at || "").slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section data-testid="dash-scripts-section" className="mt-10">
          <h2 className="flex items-center gap-3 font-display text-xl font-bold text-slate-900">
            <ScrollText className="h-5 w-5 text-flame" />
            Email &amp; sales scripts
            <span className="rounded-full border border-slate-200 bg-coal/70 px-3 py-1 font-mono text-[13px] uppercase tracking-[0.12em] text-mist">
              {SALES_SCRIPTS.length}
            </span>
          </h2>
          <p className="mt-2 font-mono text-base text-faint">
            One follow-up per service page, in your voice. Swap the [brackets], copy, send from your own inbox.
          </p>
          <div className="mt-4 space-y-3">
            {SALES_SCRIPTS.map((s, i) => {
              const open = openScript === s.id;
              return (
                <div
                  key={s.id}
                  data-testid={`dash-script-card-${s.id}`}
                  className="glass overflow-hidden rounded-2xl border border-slate-200"
                >
                  <button
                    data-testid={`dash-script-toggle-${s.id}`}
                    onClick={() => setOpenScript(open ? null : s.id)}
                    className="flex w-full items-start gap-3 px-5 py-4 text-left transition-colors hover:bg-slate-50"
                  >
                    <div className="flex-1">
                      <p className="text-base font-bold text-slate-900">{s.title}</p>
                      <p className="mt-1 text-base text-faint">{s.when}</p>
                    </div>
                    <span className="mt-1 font-mono text-base text-flame">{open ? "HIDE" : "OPEN"}</span>
                  </button>
                  {open ? (
                    <div className="border-t border-slate-200 px-5 py-4">
                      <p className="text-base text-faint">
                        <span className="font-mono uppercase tracking-[0.15em]">Subject:</span>{" "}
                        <span className="font-semibold text-slate-900">{s.subject}</span>
                      </p>
                      <pre className="mt-3 max-h-72 overflow-y-auto whitespace-pre-wrap rounded-xl border border-slate-200 bg-coal/60 p-4 font-sans text-base leading-relaxed text-slate-800">
{s.body}
                      </pre>
                      <button
                        data-testid={`dash-script-copy-${s.id}`}
                        onClick={() => copyScript(s)}
                        className={`mt-3 inline-flex items-center gap-2 rounded-2xl px-4 py-2 font-mono text-base font-semibold uppercase tracking-[0.15em] transition-all duration-300 ${
                          copied === s.id
                            ? "bg-emerald-600 text-white"
                            : "bg-flame text-obsidian hover:shadow-[0_0_20px_rgba(255,85,0,0.4)]"
                        }`}
                      >
                        {copied === s.id ? (
                          <>
                            <Check className="h-4 w-4" /> Copied
                          </>
                        ) : (
                          <>
                            <Copy className="h-4 w-4" /> Copy script
                          </>
                        )}
                      </button>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </section>

        <section className="mt-10 pb-16">
          <h2 className="flex items-center gap-3 font-display text-xl font-bold text-slate-900">
            <PenLine className="h-5 w-5 text-flame" />
            Blog — SEO tips
          </h2>
          <p className="mt-2 font-mono text-base text-faint">
            Blank line = new paragraph · a line starting &quot;## &quot; = heading · a line starting &quot;- &quot; = bullet
          </p>
          <form
            data-testid="dash-post-form"
            onSubmit={savePost}
            className="glass mt-4 rounded-2xl border border-slate-200 p-6"
          >
            <label
              htmlFor="post-title"
              className="font-mono text-base font-medium uppercase tracking-[0.22em] text-slate-800"
            >
              Title
            </label>
            <input
              id="post-title"
              data-testid="dash-post-title-input"
              type="text"
              required
              minLength={3}
              maxLength={140}
              value={postForm.title}
              onChange={(e) => setPostForm({ ...postForm, title: e.target.value })}
              placeholder="e.g. Five minutes to a better Google Business Profile"
              className="mt-2 w-full rounded-lg border border-slate-300 bg-slate-50 px-4 py-3 text-lg text-slate-900 placeholder:text-slate-700 outline-none transition-colors focus:border-flame"
            />
            <label
              htmlFor="post-body"
              className="mt-4 block font-mono text-base font-medium uppercase tracking-[0.22em] text-slate-800"
            >
              Post
            </label>
            <textarea
              id="post-body"
              data-testid="dash-post-body-input"
              required
              rows={8}
              value={postForm.body}
              onChange={(e) => setPostForm({ ...postForm, body: e.target.value })}
              placeholder={"Write the tip here.\n\n## A heading\n\n- A bullet point"}
              className="mt-2 w-full resize-y rounded-lg border border-slate-300 bg-slate-50 px-4 py-3 text-lg leading-relaxed text-slate-900 placeholder:text-slate-700 outline-none transition-colors focus:border-flame"
            />
            <div className="mt-4 flex flex-wrap items-center gap-4">
              <label className="flex items-center gap-2 text-base text-slate-800">
                <input
                  type="checkbox"
                  data-testid="dash-post-published-checkbox"
                  checked={postForm.published}
                  onChange={(e) => setPostForm({ ...postForm, published: e.target.checked })}
                  className="h-4 w-4 accent-[#FF5500]"
                />
                Published (visible on the site)
              </label>
              <div className="ml-auto flex gap-3">
                {editingId && (
                  <button
                    type="button"
                    data-testid="dash-post-cancel-button"
                    onClick={resetPostForm}
                    className="rounded-2xl border border-slate-300 px-5 py-2.5 font-mono text-base font-semibold uppercase tracking-[0.15em] text-slate-900 transition-colors hover:border-flame/60 hover:text-flame"
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="submit"
                  data-testid="dash-post-save-button"
                  disabled={savingPost}
                  className="rounded-2xl bg-flame px-5 py-2.5 font-mono text-base font-semibold uppercase tracking-[0.15em] text-obsidian transition-all duration-300 hover:shadow-[0_0_28px_rgba(255,85,0,0.45)] disabled:opacity-60"
                >
                  {savingPost ? "Saving…" : editingId ? "Save changes" : "Publish post"}
                </button>
              </div>
            </div>
          </form>
          {posts === null ? (
            <p className="mt-4 flex items-center gap-2 text-base text-faint">
              <Loader2 className="h-4 w-4 animate-spin text-flame" /> Loading…
            </p>
          ) : posts.length === 0 ? (
            <p
              data-testid="dash-empty-posts"
              className="mt-4 rounded-xl border border-slate-200 bg-coal/60 px-5 py-6 text-base text-mist"
            >
              No posts yet — write your first tip above and hit publish.
            </p>
          ) : (
            <div className="mt-4 space-y-2">
              {posts.map((p, i) => (
                <div
                  key={p.id || i}
                  data-testid={`dash-post-row-${i}`}
                  className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-coal/70 px-5 py-3"
                >
                  <span
                    className={`rounded-full px-2.5 py-0.5 font-mono text-[13px] uppercase tracking-[0.1em] ${
                      p.published
                        ? "border border-flame/40 bg-flame/10 text-flame"
                        : "border border-slate-300 text-faint"
                    }`}
                  >
                    {p.published ? "Live" : "Draft"}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-base font-semibold text-slate-900">
                    {p.title}
                  </span>
                  <a
                    href={`/blog/${p.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`dash-post-view-${i}`}
                    className="flex items-center gap-1.5 font-mono text-[13px] uppercase tracking-[0.15em] text-mist transition-colors hover:text-flame"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    View
                  </a>
                  <button
                    data-testid={`dash-post-edit-${i}`}
                    onClick={() => startEditPost(p)}
                    className="flex items-center gap-1.5 font-mono text-[13px] uppercase tracking-[0.15em] text-mist transition-colors hover:text-flame"
                  >
                    <PenLine className="h-3.5 w-3.5" />
                    Edit
                  </button>
                  <button
                    data-testid={`dash-post-delete-${i}`}
                    onClick={() => deletePost(p)}
                    className="flex items-center gap-1.5 font-mono text-[13px] uppercase tracking-[0.15em] text-mist transition-colors hover:text-red-300"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Delete
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        <section data-testid="dash-wins-section" className="mt-10 pb-16">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="flex items-center gap-3 font-display text-xl font-bold text-slate-900">
              <TrendingUp className="h-5 w-5 text-flame" />
              Wins at a glance
            </h2>
            {google === null ? null : google.connected ? (
              <div className="flex items-center gap-3">
                <span data-testid="wins-google-connected" className="font-mono text-[13px] uppercase tracking-[0.12em] text-emerald-600">
                  Google connected{google.last_sync ? ` · synced ${String(google.last_sync).slice(0, 10)}` : ""}
                </span>
                <button
                  data-testid="wins-sync-google"
                  onClick={syncGoogle}
                  disabled={syncing}
                  className="rounded-2xl border-2 border-flame px-3 py-1.5 font-mono text-[13px] font-semibold uppercase tracking-[0.12em] text-flame transition-all hover:bg-flame hover:text-obsidian disabled:opacity-60"
                >
                  {syncing ? "Syncing…" : "Sync now"}
                </button>
              </div>
            ) : (
              <button
                data-testid="wins-connect-google"
                onClick={connectGoogle}
                className="rounded-2xl border-2 border-flame px-4 py-1.5 font-mono text-[13px] font-semibold uppercase tracking-[0.12em] text-flame transition-all hover:bg-flame hover:text-obsidian"
              >
                Connect Google Analytics
              </button>
            )}
          </div>
          {typeof window !== "undefined" && window.location.search.includes("google=notconfigured") ? (
            <p data-testid="wins-google-notconfigured" className="mt-3 rounded-lg border border-flame/30 bg-flame/5 px-3 py-2 text-base text-mist">
              The Google keys aren't set up yet — finish the Cloud Console steps, save them with ZD, then this button will work.
            </p>
          ) : null}
          {typeof window !== "undefined" && window.location.search.includes("google=badclientjson") ? (
            <p data-testid="wins-google-badclientjson" className="mt-3 rounded-lg border border-flame/30 bg-flame/5 px-3 py-2 text-base text-mist">
              The saved Google client JSON isn't valid — replace it with the full OAuth client download (the one that starts with {"{"} and includes client_id and client_secret), then try Connect again.
            </p>
          ) : null}
          <p className="mt-2 font-mono text-base text-faint">
            Conversions count as HighLevel forms come in — log traffic and keyword wins as you spot them.
          </p>
          <div className="mt-4 grid gap-4 lg:grid-cols-3">
            <div
              data-testid="dash-wins-bucket-traffic"
              className="glass rounded-2xl border border-slate-200 p-5"
            >
              <p className="flex items-center gap-2 text-base font-bold text-slate-900">
                <TrendingUp className="h-4 w-4 text-flame" /> Traffic
              </p>
              <div className="mt-3 space-y-2">
                {wins === null ? (
                  <p className="flex items-center gap-2 text-base text-faint"><Loader2 className="h-4 w-4 animate-spin text-flame" /> Loading…</p>
                ) : wins.filter((x) => x.bucket === "traffic").length === 0 ? (
                  <p data-testid="dash-wins-empty-traffic" className="rounded-lg border border-slate-200 bg-coal/60 px-3 py-3 text-base text-faint">
                    e.g. Organic visits +18% this month (GA4)
                  </p>
                ) : (
                  wins.filter((x) => x.bucket === "traffic").slice(0, 3).map((x, i) => (
                    <div key={x.id} data-testid={`dash-wins-row-traffic-${i}`} className="flex items-start gap-2 rounded-lg border border-slate-100 bg-coal/60 px-3 py-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-base leading-snug text-slate-900">{x.text}</p>
                        <p className="mt-0.5 font-mono text-[13px] text-faint">
                          {String(x.created_at).slice(0, 10)}
                          {x.source === "auto" ? <span className="ml-2 rounded-full bg-slate-900/10 px-2 py-0.5 text-faint">AUTO</span> : null}
                          {daysAgo(x.created_at) < 7 ? <span className="ml-2 rounded-full bg-flame/10 px-2 py-0.5 text-flame">NEW</span> : null}
                        </p>
                      </div>
                      <button data-testid={`dash-wins-delete-traffic-${i}`} onClick={() => deleteWin(x.id, "traffic")} className="mt-0.5 text-faint transition-colors hover:text-red-400">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>
              <div className="mt-3 flex gap-2">
                <input
                  data-testid="dash-wins-input-traffic"
                  value={winText.traffic}
                  onChange={(e) => setWinText((p) => ({ ...p, traffic: e.target.value }))}
                  placeholder="Log a traffic win…"
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-base text-slate-900 placeholder:text-faint outline-none transition-colors focus:border-flame"
                />
                <button
                  data-testid="dash-wins-add-traffic"
                  onClick={() => addWin("traffic")}
                  disabled={winBusy === "traffic" || (winText.traffic || "").trim().length < 3}
                  className="flex shrink-0 items-center gap-1 rounded-2xl bg-flame px-3 py-2 font-mono text-[13px] font-semibold uppercase tracking-[0.12em] text-obsidian transition-all hover:shadow-[0_0_16px_rgba(255,85,0,0.4)] disabled:opacity-50"
                >
                  <Plus className="h-4 w-4" /> Add
                </button>
              </div>
            </div>

            <div
              data-testid="dash-wins-bucket-conversions"
              className="glass rounded-2xl border border-slate-200 p-5"
            >
              <p className="flex items-center gap-2 text-base font-bold text-slate-900">
                <CalendarCheck className="h-4 w-4 text-flame" /> Conversions
              </p>
              <div className="mt-3 space-y-2">
                <div data-testid="dash-wins-conv-leads" className="flex items-center justify-between rounded-lg border border-slate-100 bg-coal/60 px-3 py-2">
                  <span className="text-base text-slate-900">New leads (7 days)</span>
                  <span className="flex items-center gap-2 font-mono text-base font-bold text-slate-900">
                    {leads ? leadsThisWeek : "…"}
                    <span className={leadsDelta > 0 ? "text-base text-emerald-600" : leadsDelta < 0 ? "text-base text-red-400" : "text-base text-faint"}>
                      {leads ? (leadsDelta > 0 ? `▲ +${leadsDelta}` : leadsDelta < 0 ? `▼ ${leadsDelta}` : "—") : ""}
                    </span>
                  </span>
                </div>
                <div data-testid="dash-wins-conv-bookings" className="flex items-center justify-between rounded-lg border border-slate-100 bg-coal/60 px-3 py-2">
                  <span className="text-base text-slate-900">Bookings (30 days)</span>
                  <span className="font-mono text-base font-bold text-slate-900">{bookings ? bookings30 : "…"}</span>
                </div>
                <div data-testid="dash-wins-conv-checklist" className="flex items-center justify-between rounded-lg border border-slate-100 bg-coal/60 px-3 py-2">
                  <span className="text-base text-slate-900">Checklist sign-ups</span>
                  <span className="font-mono text-base font-bold text-slate-900">{checklist ? checklist.length : "…"}</span>
                </div>
                {wins === null
                  ? null
                  : wins
                      .filter((x) => x.bucket === "conversions")
                      .slice(0, 3)
                      .map((x, i) => (
                        <div key={x.id} data-testid={`dash-wins-row-conversions-${i}`} className="flex items-start gap-2 rounded-lg border border-slate-100 bg-coal/60 px-3 py-2">
                          <div className="min-w-0 flex-1">
                            <p className="text-base leading-snug text-slate-900">{x.text}</p>
                            <p className="mt-0.5 font-mono text-[13px] text-faint">
                              {String(x.created_at).slice(0, 10)}
                              {x.source === "auto" ? <span className="ml-2 rounded-full bg-slate-900/10 px-2 py-0.5 text-faint">AUTO</span> : null}
                              {daysAgo(x.created_at) < 7 ? <span className="ml-2 rounded-full bg-flame/10 px-2 py-0.5 text-flame">NEW</span> : null}
                            </p>
                          </div>
                          <button data-testid={`dash-wins-delete-conversions-${i}`} onClick={() => deleteWin(x.id, "conversions")} className="mt-0.5 text-faint transition-colors hover:text-red-400">
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
              </div>
              <div className="mt-3 flex gap-2">
                <input
                  data-testid="dash-wins-input-conversions"
                  value={winText.conversions}
                  onChange={(e) => setWinText((p) => ({ ...p, conversions: e.target.value }))}
                  placeholder="Log a HighLevel conversion…"
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-base text-slate-900 placeholder:text-faint outline-none transition-colors focus:border-flame"
                />
                <button
                  data-testid="dash-wins-add-conversions"
                  onClick={() => addWin("conversions")}
                  disabled={winBusy === "conversions" || (winText.conversions || "").trim().length < 3}
                  className="flex shrink-0 items-center gap-1 rounded-2xl bg-flame px-3 py-2 font-mono text-[13px] font-semibold uppercase tracking-[0.12em] text-obsidian transition-all hover:shadow-[0_0_16px_rgba(255,85,0,0.4)] disabled:opacity-50"
                >
                  <Plus className="h-4 w-4" /> Add
                </button>
              </div>
              {hlToken ? (
                <div data-testid="dash-wins-hl-webhook" className="mt-3 rounded-lg border border-slate-200 bg-coal/60 px-3 py-2">
                  <p className="text-[13px] leading-relaxed text-faint">
                    Auto-feed: paste this webhook URL into a HighLevel workflow (form submitted / appointment booked) and every one counts here by itself.
                  </p>
                  <div className="mt-2 flex items-center gap-2">
                    <code data-testid="dash-wins-hl-url" className="min-w-0 flex-1 truncate rounded bg-white px-2 py-1.5 font-mono text-[12px] text-slate-700">
                      {`${API}/hl/conversion?token=${hlToken}`}
                    </code>
                    <button
                      data-testid="dash-wins-hl-copy"
                      onClick={() => {
                        if (navigator.clipboard) navigator.clipboard.writeText(`${API}/hl/conversion?token=${hlToken}`);
                        setCopied("hl-webhook");
                      }}
                      className="shrink-0 rounded-2xl border-2 border-flame px-3 py-1 font-mono text-[12px] font-semibold uppercase tracking-[0.12em] text-flame transition-all hover:bg-flame hover:text-obsidian"
                    >
                      {copied === "hl-webhook" ? "Copied" : "Copy"}
                    </button>
                  </div>
                </div>
              ) : null}
              <p className="mt-3 text-[13px] leading-relaxed text-faint">
                Snapshot requests and checklist emails count as they come in. Home-page bookings and the contact form now live in HighLevel — log those here, or connect the webhook so they count automatically.
              </p>
            </div>

            <div
              data-testid="dash-wins-bucket-seo"
              className="glass rounded-2xl border border-slate-200 p-5"
            >
              <p className="flex items-center gap-2 text-base font-bold text-slate-900">
                <Search className="h-4 w-4 text-flame" /> Keywords &amp; SEO
              </p>
              <div className="mt-3 space-y-2">
                {wins === null ? (
                  <p className="flex items-center gap-2 text-base text-faint"><Loader2 className="h-4 w-4 animate-spin text-flame" /> Loading…</p>
                ) : wins.filter((x) => x.bucket === "seo").length === 0 ? (
                  <p data-testid="dash-wins-empty-seo" className="rounded-lg border border-slate-200 bg-coal/60 px-3 py-3 text-base text-faint">
                    e.g. "water heater repair olathe": #12 → #7
                  </p>
                ) : (
                  wins.filter((x) => x.bucket === "seo").slice(0, 3).map((x, i) => (
                    <div key={x.id} data-testid={`dash-wins-row-seo-${i}`} className="flex items-start gap-2 rounded-lg border border-slate-100 bg-coal/60 px-3 py-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-base leading-snug text-slate-900">{x.text}</p>
                        <p className="mt-0.5 font-mono text-[13px] text-faint">
                          {String(x.created_at).slice(0, 10)}
                          {x.source === "auto" ? <span className="ml-2 rounded-full bg-slate-900/10 px-2 py-0.5 text-faint">AUTO</span> : null}
                          {daysAgo(x.created_at) < 7 ? <span className="ml-2 rounded-full bg-flame/10 px-2 py-0.5 text-flame">NEW</span> : null}
                        </p>
                      </div>
                      <button data-testid={`dash-wins-delete-seo-${i}`} onClick={() => deleteWin(x.id, "seo")} className="mt-0.5 text-faint transition-colors hover:text-red-400">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>
              <div className="mt-3 flex gap-2">
                <input
                  data-testid="dash-wins-input-seo"
                  value={winText.seo}
                  onChange={(e) => setWinText((p) => ({ ...p, seo: e.target.value }))}
                  placeholder="Log a keyword or SEO win…"
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-base text-slate-900 placeholder:text-faint outline-none transition-colors focus:border-flame"
                />
                <button
                  data-testid="dash-wins-add-seo"
                  onClick={() => addWin("seo")}
                  disabled={winBusy === "seo" || (winText.seo || "").trim().length < 3}
                  className="flex shrink-0 items-center gap-1 rounded-2xl bg-flame px-3 py-2 font-mono text-[13px] font-semibold uppercase tracking-[0.12em] text-obsidian transition-all hover:shadow-[0_0_16px_rgba(255,85,0,0.4)] disabled:opacity-50"
                >
                  <Plus className="h-4 w-4" /> Add
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
